import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useRouter, useNavigate, stripSearchParams } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Star, User as UserIcon, ExternalLink, Link2, Check, Share2, QrCode, Ticket } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BackButton } from "@/components/BackButton";
import { ProfileQrCode } from "@/components/ProfileQrCode";
import { getPublicUserProfile, type UserReviewItem } from "@/lib/reviews.functions";
import { useReviewsT } from "@/lib/reviews-i18n";
import { useT } from "@/lib/i18n";
import { useProfileT } from "@/lib/profile-i18n";
import { useAuth } from "@/hooks/useAuth";

const SORT_KEYS = ["newest", "oldest", "highest", "lowest", "editedNewest", "editedOldest"] as const;
const FILTER_KEYS = ["all", "withComment", "high", "low"] as const;

type SortKey = (typeof SORT_KEYS)[number];
type FilterKey = (typeof FILTER_KEYS)[number];

type SearchParams = {
  revSort?: SortKey;
  revFilter?: FilterKey;
};

const isSortKey = (v: unknown): v is SortKey => typeof v === "string" && SORT_KEYS.includes(v as SortKey);
const isFilterKey = (v: unknown): v is FilterKey =>
  typeof v === "string" && FILTER_KEYS.includes(v as FilterKey);

const profileQuery = (userId: string) =>
  queryOptions({
    queryKey: ["public-user-profile", userId],
    queryFn: () => getPublicUserProfile({ data: { userId } }),
  });

export const Route = createFileRoute("/uzivatel/$userId")({
  validateSearch: (s: Record<string, unknown>): SearchParams => ({
    revSort: isSortKey(s.revSort) ? s.revSort : undefined,
    revFilter: isFilterKey(s.revFilter) ? s.revFilter : undefined,
  }),
  search: {
    middlewares: [stripSearchParams({ revSort: "newest", revFilter: "all" })],
  },
  loader: async ({ params, context }) => {
    await context.queryClient.ensureQueryData(profileQuery(params.userId));
  },
  head: () => ({
    meta: [
      { title: "User – Cerveza.International" },
      { name: "description", content: "Public user profile and their reviews." },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "User – Cerveza.International" },
      { property: "og:description", content: "Public user profile and their reviews." },
    ],
  }),
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return <ProfileError message={error.message} onRetry={() => { reset(); router.invalidate(); }} />;
  },
  notFoundComponent: () => <ProfileNotFound />,
  component: PublicUserProfilePage,
});

function PublicUserProfilePage() {
  const { userId } = Route.useParams();
  const { data } = useSuspenseQuery(profileQuery(userId));
  const t = useReviewsT();
  const pt = useProfileT();
  const { user } = useAuth();
  const isSelf = user?.id === userId;
  const { lang } = useT();
  const { revSort = "newest", revFilter = "all" } = Route.useSearch();
  const navigate = useNavigate({ from: "/uzivatel/$userId" });
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (data.referralCode) url.searchParams.set("ref", data.referralCode);
    setShareUrl(url.toString());
  }, [data.referralCode, userId]);

  const dateFmt = (iso: string) => {
    try {
      return new Intl.DateTimeFormat(lang, { dateStyle: "medium" }).format(new Date(iso));
    } catch {
      return iso.slice(0, 10);
    }
  };

  const displayName = data.displayName?.trim() || t("anon");

  const setSortKey = (sort: SortKey) =>
    navigate({ search: (prev: SearchParams) => ({ ...prev, revSort: sort }) });
  const setFilterKey = (filter: FilterKey) =>
    navigate({ search: (prev: SearchParams) => ({ ...prev, revFilter: filter }) });

  const buildShareUrl = () => {
    const url = new URL(window.location.href);
    // Attach the profile owner's referral code so any new sign-up via this
    // link is attributed to them (first-touch, 24h window; see referrals.functions).
    if (data.referralCode) url.searchParams.set("ref", data.referralCode);
    return url.toString();
  };

  const handleCopyLink = async () => {
    if (typeof window === "undefined" || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(buildShareUrl());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const handleQrDownload = (dataUrl: string) => {
    if (typeof window === "undefined") return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `profile-qr-${userId}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleQrDownloadSvg = (svg: string) => {
    if (typeof window === "undefined") return;
    const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `profile-qr-${userId}.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleShare = async () => {
    if (typeof window === "undefined") return;
    const shareUrl = buildShareUrl();
    if (canNativeShare) {
      try {
        await navigator.share({ title: displayName, text: displayName, url: shareUrl });
        return;
      } catch (err) {
        // User cancelled or share failed — fall through to clipboard copy.
        if ((err as DOMException)?.name === "AbortError") return;
      }
    }
    await handleCopyLink();
  };


  const filteredReviews = useSortedFilteredReviews(data.reviews, revSort, revFilter);

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
      <BackButton />

      <Card className="p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-earth/10 text-brand-earth">
              <UserIcon className="h-7 w-7" />
            </div>
            <div>
              <h1 className="font-serif text-2xl font-semibold text-brand-earth">{displayName}</h1>
              {data.profile?.headline && (
                <p className="text-sm text-foreground/80">{data.profile.headline}</p>
              )}
              <p className="text-sm text-muted-foreground">
                {data.summary.count > 0
                  ? t("summary", {
                      avg: data.summary.avg?.toFixed(1) ?? "–",
                      count: data.summary.count,
                    })
                  : t("summaryNone")}
              </p>
            </div>
          </div>
          {isSelf && (
            <Button variant="outline" size="sm" asChild>
              <Link to="/profil">{pt("editProfile")}</Link>
            </Button>
          )}
        </div>
      </Card>

      <Card className="p-4 sm:p-6">
        <h2 className="mb-3 font-serif text-lg font-semibold text-brand-earth">
          {pt("aboutTitle")}
        </h2>
        {!data.profile ? (
          <p className="text-sm italic text-muted-foreground">{pt("private")}</p>
        ) : (
          <div className="space-y-3 text-sm">
            {data.profile.bio ? (
              <p className="whitespace-pre-wrap text-foreground/90">{data.profile.bio}</p>
            ) : (
              <p className="italic text-muted-foreground">{pt("noInfo")}</p>
            )}
            <dl className="grid gap-2 sm:grid-cols-2">
              {data.profile.city && (
                <div>
                  <dt className="text-xs text-muted-foreground">{pt("city")}</dt>
                  <dd>{data.profile.city}</dd>
                </div>
              )}
              {data.profile.country && (
                <div>
                  <dt className="text-xs text-muted-foreground">{pt("country")}</dt>
                  <dd>{data.profile.country}</dd>
                </div>
              )}
              {data.profile.websiteUrl && (
                <div>
                  <dt className="text-xs text-muted-foreground">{pt("website")}</dt>
                  <dd>
                    <a
                      href={data.profile.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-brand-terracotta hover:underline"
                    >
                      {data.profile.websiteUrl}
                      <ExternalLink className="h-3 w-3" aria-hidden />
                    </a>
                  </dd>
                </div>
              )}
              {data.profile.memberSince && (
                <div>
                  <dt className="text-xs text-muted-foreground">&nbsp;</dt>
                  <dd className="text-muted-foreground">
                    {pt("memberSince", { date: dateFmt(data.profile.memberSince) })}
                  </dd>
                </div>
              )}
            </dl>
            {data.profile.isVakantOwner && (
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary/5 p-3">
                <Ticket className="h-4 w-4 text-primary" aria-hidden />
                <Badge variant="secondary">{pt("vakantOwner")}</Badge>
                <span>{pt("vacantsCount", { count: data.profile.vakantyPurchased })}</span>
              </div>
            )}
          </div>
        )}
      </Card>


      <Card className="p-4 sm:p-6">
        <h2 className="mb-3 font-serif text-lg font-semibold text-brand-earth">
          {t("authorReviewsTitle")}
        </h2>
        {data.reviews.length === 0 ? (
          <p className="text-sm italic text-muted-foreground">{t("authorNoReviews")}</p>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <div className="min-w-[10rem]">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  {t("sortLabel")}
                </label>
                <Select value={revSort} onValueChange={(v) => setSortKey(v as SortKey)}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">{t("sortNewest")}</SelectItem>
                    <SelectItem value="oldest">{t("sortOldest")}</SelectItem>
                    <SelectItem value="highest">{t("sortHighest")}</SelectItem>
                    <SelectItem value="lowest">{t("sortLowest")}</SelectItem>
                    <SelectItem value="editedNewest">{t("sortEditedNewest")}</SelectItem>
                    <SelectItem value="editedOldest">{t("sortEditedOldest")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[10rem]">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  {t("filterLabel")}
                </label>
                <Select value={revFilter} onValueChange={(v) => setFilterKey(v as FilterKey)}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("filterAll")}</SelectItem>
                    <SelectItem value="withComment">{t("filterWithComment")}</SelectItem>
                    <SelectItem value="high">{t("filterHigh")}</SelectItem>
                    <SelectItem value="low">{t("filterLow")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="ml-auto flex flex-wrap items-end gap-2">
                {canNativeShare && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 text-xs"
                    onClick={handleShare}
                    aria-label={t("share")}
                  >
                    <Share2 className="h-3.5 w-3.5" aria-hidden />
                    {t("share")}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 text-xs"
                  onClick={handleCopyLink}
                  aria-label={copied ? t("copied") : t("copyLink")}
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
                  ) : (
                    <Link2 className="h-3.5 w-3.5" aria-hidden />
                  )}
                  {copied ? t("copied") : t("copyLink")}
                </Button>
                <Button
                  type="button"
                  variant={qrOpen ? "secondary" : "outline"}
                  size="sm"
                  className="h-8 gap-1.5 text-xs"
                  onClick={() => setQrOpen((v) => !v)}
                  aria-label={qrOpen ? t("hideQr") : t("showQr")}
                  aria-expanded={qrOpen}
                >
                  <QrCode className="h-3.5 w-3.5" aria-hidden />
                  {qrOpen ? t("hideQr") : t("showQr")}
                </Button>
              </div>
            </div>
            {qrOpen && shareUrl && (
              <figure className="mb-4 flex flex-col items-center gap-2 rounded-lg border border-dashed border-brand-earth/20 bg-muted/30 p-4 sm:items-start">
                <ProfileQrCode
                  url={shareUrl}
                  size={180}
                  ariaLabel={t("qrTitle")}
                  downloadLabel={t("downloadQr")}
                  onDownload={handleQrDownload}
                  downloadSvgLabel={t("downloadQrSvg")}
                  onDownloadSvg={handleQrDownloadSvg}
                />
                <dl className="w-full space-y-1 text-xs">
                  <div className="flex flex-col gap-0.5">
                    <dt className="font-medium text-muted-foreground">{t("urlPreview")}</dt>
                    <dd>
                      <a
                        href={shareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all font-mono text-brand-earth underline-offset-2 hover:underline"
                      >
                        {shareUrl}
                      </a>
                    </dd>
                  </div>
                  <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2">
                    <dt className="font-medium text-muted-foreground">{t("fileName")}</dt>
                    <dd className="break-all font-mono">
                      profile-qr-{userId}.png / .svg
                    </dd>
                  </div>
                </dl>
                <figcaption className="max-w-[14rem] text-center text-xs leading-tight text-muted-foreground sm:text-left">
                  {t("qrHint")}
                </figcaption>
              </figure>
            )}
            {filteredReviews.length === 0 ? (
              <p className="text-sm italic text-muted-foreground">{t("noMatch")}</p>
            ) : (
              <ul className="divide-y divide-brand-earth/10">
                {filteredReviews.map((r) => (
                  <UserReviewRow key={r.id} r={r} dateLabel={dateFmt(r.created_at)} t={t} />
                ))}
              </ul>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function useSortedFilteredReviews(
  reviews: UserReviewItem[],
  sortKey: SortKey,
  filterKey: FilterKey,
) {
  return useMemo(() => {
    const arr = reviews.filter((r: UserReviewItem) => {
      if (filterKey === "withComment") return !!r.comment?.trim();
      if (filterKey === "high") return r.rating >= 8;
      if (filterKey === "low") return r.rating <= 4;
      return true;
    });
    const cmp: Record<SortKey, (a: UserReviewItem, b: UserReviewItem) => number> = {
      newest: (a, b) => b.created_at.localeCompare(a.created_at),
      oldest: (a, b) => a.created_at.localeCompare(b.created_at),
      highest: (a, b) => b.rating - a.rating || b.created_at.localeCompare(a.created_at),
      lowest: (a, b) => a.rating - b.rating || b.created_at.localeCompare(a.created_at),
      editedNewest: (a, b) =>
        (b.updated_at ?? b.created_at).localeCompare(a.updated_at ?? a.created_at),
      editedOldest: (a, b) =>
        (a.updated_at ?? a.created_at).localeCompare(b.updated_at ?? b.created_at),
    };
    return [...arr].sort(cmp[sortKey]);
  }, [reviews, sortKey, filterKey]);
}

function UserReviewRow({
  r,
  dateLabel,
  t,
}: {
  r: UserReviewItem;
  dateLabel: string;
  t: ReturnType<typeof useReviewsT>;
}) {
  const typeLabel =
    r.target_type === "business"
      ? t("targetBusiness")
      : r.target_type === "supplier"
        ? t("targetSupplier")
        : t("targetMenuItem");
  const label = r.target_label || typeLabel;

  return (
    <li className="py-3">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1 text-sm font-medium">
          <Star className="h-4 w-4 fill-amber-400 stroke-amber-500" aria-hidden />
          {r.rating}/10
        </div>
        <Badge variant="outline" className="text-xs">
          {typeLabel}
        </Badge>
        <span className="text-xs text-muted-foreground">
          · <time dateTime={r.created_at}>{dateLabel}</time>
        </span>
      </div>
      <div className="text-sm">
        {r.target_href ? (
          <Link
            to={r.target_href}
            className="inline-flex items-center gap-1 text-brand-terracotta hover:underline"
          >
            {label}
            <ExternalLink className="h-3 w-3" />
          </Link>
        ) : (
          <span className="text-muted-foreground">{label}</span>
        )}
      </div>
      {r.comment?.trim() && (
        <p className="mt-1 whitespace-pre-wrap text-sm text-foreground/90">{r.comment}</p>
      )}
    </li>
  );
}

function ProfileError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const t = useReviewsT();
  return (
    <div className="mx-auto max-w-3xl p-6">
      <Card className="p-6 space-y-3">
        <h1 className="text-lg font-semibold">{t("profileLoadError")}</h1>
        <p className="text-sm text-muted-foreground">{message}</p>
        <Button size="sm" onClick={onRetry}>{t("retry")}</Button>
      </Card>
    </div>
  );
}

function ProfileNotFound() {
  const t = useReviewsT();
  return (
    <div className="mx-auto max-w-3xl p-6">
      <Card className="p-6">{t("notFound")}</Card>
    </div>
  );
}
